import { Button, Field, Input, Text } from '@components/ui';
import {
  type ChangePasswordFormValues,
  changePasswordSchema,
} from '@features/auth/schemas/changePasswordSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiErrorMessage } from '@lib/api/apiError';
import { useChangePasswordMutation } from '@lib/api/profile';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';


interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ChangePasswordDialog = ({
  open,
  onClose,
}: ChangePasswordDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const changePassword = useChangePasswordMutation();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<ChangePasswordFormValues>({
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const handleClose = (): void => {
    if (!changePassword.isPending) {
      onClose();
    }
  };

  const onSubmit = async (values: ChangePasswordFormValues): Promise<void> => {
    try {
      await changePassword.mutateAsync({
        currentPassword: values.currentPassword,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      toast.success(String(t('profile.passwordUpdated')));
      reset();
      onClose();
    } catch (err: unknown) {
      const msg =
        apiErrorMessage(err) ?? String(t('profile.passwordUpdateFailed'));
      toast.error(msg);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
        <Text as="span" variant="subtitle">
          {t('auth.changePassword')}
        </Text>
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ px: 3, pt: 1, pb: 1 }}>
          <Field
            label={t('profile.currentPassword')}
            error={
              errors.currentPassword
                ? t(errors.currentPassword.message ?? '')
                : undefined
            }
          >
            <Input
              type="password"
              invalid={Boolean(errors.currentPassword)}
              placeholder={t('profile.currentPasswordPlaceholder')}
              autoComplete="current-password"
              autoFocus
              {...register('currentPassword')}
            />
          </Field>
          <Field
            label={t('profile.newPassword')}
            error={
              errors.password ? t(errors.password.message ?? '') : undefined
            }
          >
            <Input
              type="password"
              invalid={Boolean(errors.password)}
              placeholder={t('profile.newPasswordPlaceholder')}
              autoComplete="new-password"
              {...register('password')}
            />
          </Field>
          <Field
            label={t('profile.confirmPassword')}
            error={
              errors.confirmPassword
                ? t(errors.confirmPassword.message ?? '')
                : undefined
            }
          >
            <Input
              type="password"
              invalid={Boolean(errors.confirmPassword)}
              placeholder={t('profile.confirmPasswordPlaceholder')}
              autoComplete="new-password"
              {...register('confirmPassword')}
            />
          </Field>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={changePassword.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid}
            loading={changePassword.isPending}
          >
            {t('profile.updatePassword')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
